import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  OutlinedInput,
} from '@mui/material'
import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MdiClose from '~icons/mdi/close'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { track } from 'src/utils/tracker'
import { UIButton } from '../ui/Button'

type FormType = 'cancel-subs' | 'cancel-payment'

const cancelSubReasons = [
  'too-expensive',
  'poor-generation-quality',
  'found-a-better-service-elsewhere',
  'no-longer-in-need',
  'technical-issues',
  'dissatisfied-with-customer-support',
  'dissatisfied-with-membership-features',
  'makes-no-difference',
  'pausing-the-subscription',
]

const cancelPaymentReasons = [
  'confused-about-actual-payment-amount',
  'accidentally-clicked',
  'encountered-payment-issues',
  'concerned-about-security',
]

function MembershipSubscriptionCancelSurveyForm(props: {
  type: FormType
  onClose?: () => void
  onConfirm?: () => void
}) {
  const reasons =
    props.type === 'cancel-subs' ? cancelSubReasons : cancelPaymentReasons
  const { t } = useTranslation(['membership', 'action'])
  const [otherReason, setOtherReason] = useState('')
  const [checkBoxState, setCheckBoxState] = useState(() =>
    Object.fromEntries(reasons.map(r => [r, false])),
  )

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCheckBoxState(prev => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }))
  }

  const onSubmit = () => {
    track(`${props.type}-submit`, {
      content: otherReason,
      ...checkBoxState,
    })
    props.onConfirm?.()
  }

  const nsKey = `${props.type}-survey`

  return (
    <>
      <UIDialogHeader
        heading={t(`membership:${nsKey}.title`)}
        className="relative pr-16"
      >
        {({ heading }) => (
          <>
            {heading}
            <IconButton
              className="absolute top-3 right-3"
              onClick={props.onClose}
            >
              <MdiClose />
            </IconButton>
          </>
        )}
      </UIDialogHeader>
      <UIDialogContent>
        <FormGroup>
          {reasons.map(r => (
            <FormControlLabel
              control={<Checkbox name={r} onChange={handleCheckboxChange} />}
              key={r}
              checked={checkBoxState[r]}
              label={t(`membership:${nsKey}.reasons.${r}`)}
            />
          ))}
        </FormGroup>
        <OutlinedInput
          className="mt-4"
          fullWidth
          value={otherReason}
          onChange={e => {
            setOtherReason(e.target.value)
          }}
          inputProps={{ maxLength: 500 }}
          multiline
          placeholder={t(`membership:${nsKey}.reasons.other`)}
        />
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton variant="filled" onPress={onSubmit}>
          {t('action:submit')}
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

export const openMembershipSubscriptionSurveyDialog = async (opts: {
  type: FormType
}) => {
  await UIDialog.create({
    className: 'w-fit max-w-none',
    children: ({ close }) => (
      <MembershipSubscriptionCancelSurveyForm
        type={opts.type}
        onConfirm={() => close()}
        onClose={() => close()}
      />
    ),
  })
}
